// src/services/firestore.js
import { db, functions } from "./FireBase"; // gebruikt jouw bestaande setup :contentReference[oaicite:2]{index=2}
import { ORG_ID, ACTIVE_YEAR } from "../constants"; // gebruikt jullie constants :contentReference[oaicite:3]{index=3}
import { httpsCallable } from "firebase/functions";

import {
  // re-export helpers van firestore zodat hooks alleen uit deze service hoeven te importeren
  collection, collectionGroup, doc, getDoc, getDocs, getCountFromServer, onSnapshot, setDoc, addDoc,
  updateDoc, deleteDoc, query, where, orderBy, limit, startAfter, serverTimestamp, deleteField, writeBatch, runTransaction, Timestamp, increment
} from "firebase/firestore";

function safeDocId(value) {
  return String(value || "").replace(/\//g, "_").trim();
}

// ---- Centralized collection/doc references ----
export const colRefs = {
  users:            () => collection(db, "orgs", ORG_ID, "users"),
  allowedKisten:    () => collection(db, "orgs", ORG_ID, "allowedKisten"),
  allowedKistenYear: (year = ACTIVE_YEAR) => collection(db, "orgs", ORG_ID, "years", String(year), "allowedKisten"),
  counterHoutenKist: (year = ACTIVE_YEAR) => doc(db,"orgs", ORG_ID, "years", String(year), "counter", "houtenkist"),
  kisten:           () => collection(db, "orgs", ORG_ID, "inventory"),
  logs:             () => collection(db, "orgs", ORG_ID, "years", ACTIVE_YEAR, "logs"),
  adminActions:     () => collection(db, "orgs", ORG_ID, "years", ACTIVE_YEAR, "adminActions"),
  perceelStats: (year = ACTIVE_YEAR) => collection(db, "orgs", ORG_ID, "years", String(year), "perceelStats"),
  perceelStatsMetaDoc: (year = ACTIVE_YEAR) =>
    doc(db, "orgs", ORG_ID, "years", String(year), "perceelStats", "_meta"),
  perceelStatsDoc: (year = ACTIVE_YEAR, perceelNaam) =>
    doc(db, "orgs", ORG_ID, "years", String(year), "perceelStats", safeDocId(perceelNaam)),
  stateDoc:         () => doc(db, "orgs", ORG_ID, "state", "koeling"),
  coolingRows:      () => collection(db, "orgs", ORG_ID, "state", "koeling", "rows"),
  coolingRowDoc:    (rowId) => doc(db, "orgs", ORG_ID, "state", "koeling", "rows", safeDocId(rowId)),
  planningDoc:      () => doc(db, "orgs", ORG_ID, "state", "planning"),
  planningProgressDoc: () => doc(db, "orgs", ORG_ID, "state", "planningProgress"),
  weekOrdersStateDoc: () => doc(db, "orgs", ORG_ID, "state", "weekOrders"),
  customerOrderRequestsSignalDoc: () =>
    doc(db, "orgs", ORG_ID, "signals", "customerOrderRequests"),
  stockPalletOffersSignalDoc: () =>
    doc(db, "orgs", ORG_ID, "signals", "stockPalletOffers"),
  weekOrders:       () => collection(db, "orgs", ORG_ID, "weekOrders"),
  weekOrderDoc:     (orderId) => doc(db, "orgs", ORG_ID, "weekOrders", safeDocId(orderId)),
  weekOrderTemplates: () => collection(db, "orgs", ORG_ID, "weekOrderTemplates"),
  weekOrderTemplateDoc: (templateId) =>
    doc(db, "orgs", ORG_ID, "weekOrderTemplates", safeDocId(templateId)),
  partijDoc:        (p)   => doc(db, "orgs", ORG_ID, "years", ACTIVE_YEAR, "partijen", p),
  partijEvents:     (p)   => collection(db, "orgs", ORG_ID, "years", ACTIVE_YEAR, "partijen", p, "events"),
  orderKistdata:    (orderId) =>
     collection(db, "orgs", ORG_ID, "years", ACTIVE_YEAR, "orders", orderId, "kistdata"),
  orderKistdataGroup: () => collectionGroup(db, "kistdata"),
  machinesDoc:      (id)  => doc(db, "orgs", ORG_ID, "machines", String(id)),
  orders:           ()    => collection(db, "orgs", ORG_ID, "years", ACTIVE_YEAR, "orders"),
  ordersOpen:       (key) => doc(db, "orgs", ORG_ID, "years", ACTIVE_YEAR, "ordersOpen", String(key)),
  percelen: (year) => collection(db, "orgs", ORG_ID, "years", String(year), "percelen"),
  percelenDoc: (year, perceelNaam) => doc(db, "orgs", ORG_ID, "years", String(year), "percelen", perceelNaam),
  pallets: (year = ACTIVE_YEAR) => collection(db, "orgs", ORG_ID, "years", String(year), "pallets"),
  printJobs: () => collection(db, "orgs", ORG_ID, "years", String(ACTIVE_YEAR), "printJobs"),
  palletPrintJobs: () => collection(db, "orgs", ORG_ID, "years", String(ACTIVE_YEAR), "palletPrintJobs"),
  fustMovements: (year = ACTIVE_YEAR) => collection(db, "orgs", ORG_ID, "years", String(year), "fustMovements"),
  fustTotalsDoc: (year = ACTIVE_YEAR) => doc(db, "orgs", ORG_ID, "years", String(year), "fustState", "totals"),
  fustTypeSettingsDoc: (year = ACTIVE_YEAR) => doc(db, "orgs", ORG_ID, "years", String(year), "fustState", "typeVisibility"),
  crateLoans: () => collection(db, "orgs", ORG_ID, "crateLoans"),
  crateLoanDoc: (loanId) =>
    doc(db, "orgs", ORG_ID, "crateLoans", safeDocId(loanId)),
  printerAlertDoc: (year = ACTIVE_YEAR) => doc(db, "orgs", ORG_ID, "years", String(year), "printerAlerts", "current"),
};

// ---- Kleine, herbruikbare utils (uit App.js gehaald) ----
export const orderGroupKeyFor = (input) => {
  const s = String(input || "").toLowerCase();
  if (s === "other" || s === "machine-other") return "other";
  const num = s.startsWith("machine-") ? s.split("-")[1] : s.match(/\d+/)?.[0];
  const id = String(num || "");
  if (id === "3") return "3";
  if (id === "4") return "4";
  return "12";
};



export async function hasOpenOrderAndPartijFor(machineId) {
  const key  = orderGroupKeyFor(machineId);
  const snap = await getDoc(colRefs.ordersOpen(key));
  if (!snap.exists()) return false;
  const d = snap.data() || {};
  return !!(d.active && d.partijId);
}

// ---- Optionele mini-wrappers (houd simpel & veilig) ----
export async function getDocSafe(ref) {
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Re-export Firestore primitieven zodat je in hooks alles hier vandaan haalt
export {
  db, collection, doc, getDoc, getDocs, getCountFromServer, onSnapshot, setDoc, addDoc,
  updateDoc, deleteDoc, query, where, orderBy, limit, startAfter, serverTimestamp, deleteField, writeBatch, runTransaction, Timestamp, increment, functions, httpsCallable
};

